<template>
  <div>
    <section v-if="modalVisible">
      <deal-form
        :deal-id="dealId"
        :is-read-only="isReadOnly"
        :modal-visible="modalVisible"
        @close="closeModal"
        @close-and-refresh="closeAndRefresh"
        @save-event="saveEvent"
        @create-new="closeAndOpenModalCreate"
        :defaultTradeDate="defaultTradeDate"
        :deal-type-id="-1"
        :market-id="this.marketId"
      />
    </section>
    <section v-show="!modalVisible">
      <page-toolbar>
        <period-control
          @change="periodChange"
          :show-all-year-item="true"
          :show-all-period-item="true"
        ></period-control>
        <kendo-button
          v-if="!(isMaster || isReadOnly)"
          icon="add"
          title="Create Order"
          @click="openModalCreate"
        ></kendo-button>
        <responsible-email
          v-if="isMaster"
          v-model="responsibleEmail"
          @change="responsibleEmailChanged"
        />
        <div id="excelButtonContainer"></div>
        <v-switch
          v-model="showActive"
          :label="`Show only Call Level and Active `"
          @change="gridRefresh"
        ></v-switch>
      </page-toolbar>

      <kendo-grid
        height="calc(100vh - 170px)"
        ref="orderGrid"
        :editable="!isReadOnly && 'inline'"
        :dataSource="dataSource"
        :columns="columns"
        :pageable="true"
        :filterable="true"
        :sortable="true"
        :navigatable="true"
        :autoBind="false"
      ></kendo-grid>
    </section>
  </div>
</template>
<script>
import $ from '@/helpers/jquery-bridge'
import DealForm from '../Deal/DealForm'
import {mapGetters} from 'vuex'
import {enableExportExcel} from '@/helpers/export-helper'
import {getDataItem} from '@/helpers/kendo-helpers'
import PageToolbar from '@/components/PageToolbar'
import security from '@/mixins/security'
import categoryColumns from '@/mixins/categoryColumns'
import PeriodControl from '@/components/PeriodControl'
import {
  jsonDropDownEditor,
  dateTimePickerEditor,
  checkboxColumn,
  numericEditor,
  readOnlyIf,
} from '@/helpers/kendo-editors'
import {isBuyingEditor, baseCcyEditor} from '../Deal/editors'
import ResponsibleEmail from '@/components/ResponsibleEmail'
import params from '@/helpers/params'

export default {
  name: 'DealView',
  //props: {
  //  marketId: {type: Number, required: true},
  //},
  mixins: [security, categoryColumns],
  components: {
    DealForm,
    PageToolbar,
    PeriodControl,
    ResponsibleEmail,
  },
  beforeDestroy() {
    document.body.removeEventListener('click', this.dealLinkClick)
  },
  async mounted() {
    document.body.addEventListener('click', this.dealLinkClick, false)
    let grid = this.$refs.orderGrid.kendoWidget()
    enableExportExcel(grid, '/api/list/order/exportToExcel', $('#excelButtonContainer'))

    const {id} = params.toObject(location.search)
    if (id) {
      // Switch to master
      await this.$store.dispatch('authentication/setSelectedClientId', {selectedClientId: 0})
      this.modalVisible = true
      this.dealId = id
    }
  },
  computed: {
    ...mapGetters({clientId: 'authentication/selectedClientId'}),
    ...mapGetters({marketId: 'app/marketId'}),
    isMaster() {
      return this.clientId == 0
    },
    grid() {
      return this.$refs.orderGrid
    },
    columns() {
      return [
        {
          command: [{name: 'edit', visible: dataItem => !dataItem.dealIsConfirmed}],
          title: '&nbsp;',
          width: 60,
          hidden: this.isReadOnly,
          locked: true,
        },
        {
          field: 'dealId',
          title: 'Group Id',
          width: 100,
          attributes: {style: 'text-align: left;'},
          locked: true,
          template: kendo.template('<a href="javascript:;" class="dealCodeLink">#=dealId#</a>'),
        },
        {
          field: 'clientName',
          title: 'Client',
          width: 200,
          hidden: !this.isMaster,
        },
        {
          field: 'product',
          title: 'Product',
          width: 100,
        },
        {
          field: 'divisionName',
          title: 'Division',
          width: 100,
        },
        {
          field: 'statusName',
          title: 'Status',
          width: 100,
          editor: jsonDropDownEditor('statusId', 'statusName', '/api/list/Order/getOrderStatuses'),
        },
        checkboxColumn(
          'alarm',
          {
            title: 'Alarm',
            width: 65,
            filterable: false,
          },
          !this.editable,
          null,
        ),
        {
          title: 'Reuters',
          columns: [
            checkboxColumn(
              'isInputReuter',
              {
                title: 'Input',
                width: 65,
                filterable: false,
              },
              !this.editable,
              null,
            ),
            checkboxColumn(
              'isCancelledReuter',
              {
                title: 'Cancelled',
                width: 80,
                filterable: false,
              },
              !this.editable,
              null,
            ),
          ],
          hidden: true,
        },
        {
          field: 'orderDateTime',
          title: 'Order Date Time',
          width: 150,
          attributes: {style: 'text-align: right;'},
          format: '{0:yyyy-MM-dd HH:mm}',
          editable: dataItem => dataItem.canEdit,
          editor: dateTimePickerEditor({format: 'yyyy-MM-dd HH:mm'}, true),
        },
        {
          field: 'counterparty',
          title: 'Bank',
          width: 100,
          editor: jsonDropDownEditor(
            'counterpartyId',
            'counterpartyCode',
            '/api/list/deal/counterparties?clientId=' + this.clientId,
          ),
          editable: dataItem => dataItem.canEdit,
        },
        {
          field: 'isBuying',
          title: 'Buy / Sell',
          width: 80,
          editor: isBuyingEditor,
          template: function(item) {
            return item.isBuying ? 'Buy' : 'Sell'
          },
          editable: dataItem => dataItem.canEdit,
        },
        {
          field: 'amount',
          title: 'Amount',
          width: 150,
          format: '{0:N0}',
          attributes: {style: 'text-align: right;'},
          editable: dataItem => dataItem.canEdit,
          editor: numericEditor({
            decimals: 2,
            format: '{0:n0}',
            spinners: false,
            step: 0,
          }),
        },
        {
          field: 'ccy1',
          title: 'Com.Ccy',
          width: 100,
          editor: jsonDropDownEditor(
            'foreignCcy',
            'foreignCcy',
            '/api/list/deal/getForeignCurrencies?clientId=' +
              this.clientId +
              '&marketId=' +
              this.marketId,
            false,
          ),
          editable: dataItem => dataItem.canEdit,
        },
        {
          field: 'ccy2',
          title: 'Ccy 2',
          width: 82,
          editor: readOnlyIf(dataItem => !dataItem.canEdit, baseCcyEditor(this.clientId, 1)),
        },
        {
          field: 'orderRate',
          title: 'Order Rate',
          width: 100,
          format: '{0:N4}',
          attributes: {style: 'text-align: right;'},
          editable: dataItem => dataItem.canEdit,
          editor: numericEditor({
            decimals: 16,
            format: '{0:n4}',
            spinners: false,
            step: 0,
          }),
          filterable: {
            ui: function(element) {
              element.kendoNumericTextBox({
                decimals: 4,
                format: 'n4',
                spinners: false,
                step: 0,
              })
            },
          },
        },
        {
          field: 'executedDate',
          title: 'Executed Date',
          width: 120,
          attributes: {style: 'text-align: right;'},
          format: '{0:yyyy-MM-dd}',
          editable: dataItem => dataItem.canEdit,
        },
        {
          field: 'orderTypeName',
          title: 'Type',
          width: 150,
          attributes: {style: 'text-align: left;'},
          editor: jsonDropDownEditor(
            'orderTypeId',
            'orderTypeName',
            '/api/list/Order/getOrderTypes',
          ),
          editable: dataItem => dataItem.canEdit,
        },
        {
          title: 'GTC',
          field: 'gtCDate',
          format: '{0:yyyy-MM-dd}',
          width: 140,
          attributes: {style: 'text-align: right;'},
          editable: dataItem => dataItem.canEdit,
        },
        {
          title: 'OCO',
          field: 'ocoRate',
          width: 140,
          attributes: {style: 'text-align: right;'},
          format: '{0:n4}',
          editable: dataItem => dataItem.canEdit,
          editor: numericEditor({
            decimals: 16,
            format: '{0:n4}',
            spinners: false,
            step: 0,
          }),
          filterable: {
            ui: function(element) {
              element.kendoNumericTextBox({
                decimals: 4,
                format: 'n4',
                spinners: false,
                step: 0,
              })
            },
          },
        },
        {
          field: 'comment',
          title: 'Commentaries',
          width: 300,
          editable: dataItem => dataItem.canEdit,
        },
        {
          field: 'lastStatusChangedDateTime',
          title: 'Last Modified',
          width: 140,
          attributes: {style: 'text-align: right;'},
          format: '{0:yyyy-MM-dd HH:mm}',
        },
        {
          field: 'lastUser',
          title: 'Last User',
          width: 120,
          attributes: {style: 'text-align: left;'},
        },
        {
          field: 'responsible',
          title: 'Responsible',
          width: 232,
          attributes: {style: 'text-align: left;'},
          hidden: !this.isMaster,
        },
        {
          field: 'manager',
          title: 'Manager',
          width: 232,
          attributes: {style: 'text-align: left;'},
          hidden: !this.isMaster,
        },
        ...this.categoryColumns,
      ]
    },
    defaultTradeDate() {
      const today = kendo.toString(new Date())
      today.setHours(0, 0, 0, 0)

      const todayString = kendo.format('{0:s}', today)

      const minDate = todayString < this.endDate ? todayString : this.endDate

      return minDate
    },
    editable() {
      return !(this.role === 'Guest' || this.role === 'Standard')
    },
  },
  methods: {
    dealLinkClick({target}) {
      if (!/dealCodeLink/.test(target.className)) return
      const dataItem = getDataItem(target)
      this.modalVisible = true
      this.dealId = dataItem.dealId
    },
    openModalUpdate: function(e) {
      e.preventDefault()

      let grid = this.$refs.orderGrid.kendoWidget()
      var dataItem = grid.dataItem($(e.currentTarget).closest('tr'))
      this.modalVisible = true
      this.dealId = dataItem.dealId
    },
    closeAndOpenModalCreate() {
      this.closeModal()

      setTimeout(() => {
        this.openModalCreate()
      }, 500)
    },
    openModalCreate() {
      this.modalVisible = true
      this.dealId = 0
    },
    closeModal() {
      this.dealId = null
      this.modalVisible = false
    },
    periodChange(e) {
      this.startDate = e.startDate
      this.endDate = e.endDate
      this.gridRefresh()
    },
    responsibleEmailChanged() {
      this.gridRefresh()
    },
    gridRefresh() {
      let grid = this.$refs.orderGrid.kendoWidget()
      grid.dataSource.read()
    },
    saveEvent(dealId) {
      this.dealId = dealId
    },
    closeAndRefresh() {
      this.closeModal()
      this.refreshCategoryColumns().then(() => {
        this.gridRefresh()
      })
    },
  },
  data() {
    return {
      dealId: null,
      startDate: null,
      endDate: null,
      modalVisible: false,
      showActive: true,
      checkedItems: [],
      asOfDate: new Date(),
      responsibleEmail: 'ALL',
      dataSource: {
        type: 'aspnetmvc-ajax',
        serverPaging: true,
        serverFiltering: true,
        serverSorting: true,
        pageSize: 20,
        transport: {
          read: {
            type: 'GET',
            url: '/api/list/order',
            data: () => ({
              startDate: this.startDate,
              endDate: this.endDate,
              showActive: this.showActive,
              clientId: this.clientId,
              responsibleEmail: this.responsibleEmail,
              marketId: this.marketId,
            }),
            dataType: 'json',
          },
          create: {
            type: 'POST',
            url: '/api/list/order',
            data: () => ({clientId: this.clientId}),
            dataType: 'json',
          },
          update: {
            type: 'PUT',
            url: '/api/list/order',
            data: () => ({clientId: this.clientId}),
            dataType: 'json',
          },
          destroy: {
            type: 'DELETE',
            url: '/api/list/order',
            data: () => ({clientId: this.clientId}),
            dataType: 'json',
          },
        },
        schema: {
          errors: 'errors',
          total: 'total',
          data: 'data',
          model: {
            id: 'orderId',
            fields: {
              dealId: {type: 'number', editable: false},
              statusId: {type: 'number'},
              dealCode: {type: 'string', field: 'dealCode', editable: false},

              orderDateTime: {type: 'date', validation: {required: true}},
              divisionName: {type: 'string', editable: false},
              executedDate: {type: 'date'},
              lastStatusChangedDateTime: {type: 'date', editable: false},
              gtCDate: {type: 'date'},
              isBuying: {type: 'boolean'},
              alarm: {type: 'boolean'},
              isCancelledReuter: {type: 'boolean', editable: true},
              isInputReuter: {type: 'boolean', editable: true},
              orderRate: {type: 'number'},
              ocoRate: {type: 'number'},
              amount: {type: 'number'},
              clientName: {type: 'string', field: 'clientName', editable: false},
              lastUser: {type: 'string', editable: false},
              responsible: {type: 'string', editable: false},
              manager: {type: 'string', editable: false},
              canEdit: {type: 'boolean'},
            },
          },
        },
      },
    }
  },
}
</script>
